import { Component, OnInit } from '@angular/core';
import { amountTypeDict, Constants, frequencyDict, pdfLabelsSubmitDetails, transactionDetailLabels, transactionTypes, transactionTypesDict, yesNo, YesNo } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { selectInternalTransferInfo, selectIraFieldsIsIraTransaction, selectStagedTransactionForm, selectToAccountInfo, selectTransferType } from '../../../../../../create-request/reducers';
import { TitleCasePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { isInternalTransferForm, ItExtraPrompts } from '../../../../../../create-request/models/form/TransactionForm';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { Classifications } from 'projects/money-movement-ui/src/app/account-info-header/models';
import { InternalTransferInfo } from '../../../../../../create-request/models/internal-transfer/InternalTransferInfo';
import { selectIsPendingItSloaSetup } from 'projects/money-movement-ui/src/app/account-info-header/reducers';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-it-condensed',
  templateUrl: './transaction-details-it-condensed.component.html',
  styleUrl: './transaction-details-it-condensed.component.scss'
})
export class TransactionDetailsItCondensedComponent implements OnInit{

  readonly labels = transactionDetailLabels;
  readonly sectionTitle: string = 'Transaction details';
  readonly pdfLabelsSubmitDetails = pdfLabelsSubmitDetails;
  readonly nextBusinessDayCopy: string = Constants.nextBusinessDayCopy;
  destroy$: Subject<boolean> = new Subject<boolean>();
  amountType: string;
  amount: string;
  endDate: string;
  frequency: string;
  grossAmount: string;
  isPtrs: boolean;
  netAmount: string;
  isStandingAuthViewPage: boolean;
  isTransactionViewPage: boolean;
  transactionTypeLabel: string;
  isRecurring: boolean;
  useStandingAuth: string;
  secondProcessDate: string;
  frequencyDescription: string;
  payIncome: string;
  isIraTransaction: boolean;
  classifications: Classifications = new Classifications();
  itInfo: InternalTransferInfo;
  nextProcessDate: string;
  isPendingSloaSetup: boolean;
  transferType: string;
  formTransferType: string;
  isToAccountTrust: boolean = false;
  showIRAContributionType: boolean;
  iraContributionType: string;
  standingAuthDetails: string = '';
  isBundledStandingAuth: boolean = false;

  constructor(private store: Store, private titleCasePipe: TitleCasePipe){
    this.itInfo = new InternalTransferInfo();
  }

  ngOnInit(): void {
    this.store.select(selectIsStandingAuthViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isStandingAuthViewPage = value);
    this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isTransactionViewPage = value);
    this.store.select(selectIsPendingItSloaSetup).pipe(takeUntil(this.destroy$)).subscribe(isPendingSloaSetup => {
      this.isPendingSloaSetup = isPendingSloaSetup;
    })

    this.store.select(selectIraFieldsIsIraTransaction).pipe(takeUntil(this.destroy$)).subscribe(_isIraTransaction => this.isIraTransaction = _isIraTransaction)

    this.store.select(selectToAccountInfo).pipe(takeUntil(this.destroy$)).subscribe(toAccountInfo => {
      this.standingAuthDetails = `${toAccountInfo?.accountNumber} - ${toAccountInfo?.registrationType}`;
    });

    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectInternalTransferInfo),
      this.store.select(selectTransferType)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, itInfo, transferType]) => {
      if (form?.transactionType && isInternalTransferForm(form)) {
        if (form.transactionType === transactionTypes.internalTransfer) {
          this.itInfo = itInfo;
          let extraPrompts = form.extraPrompts;
          this.isBundledStandingAuth = extraPrompts.useBundledStandingAuth === 'yes';
          this.isToAccountTrust = itInfo.toAccountInfo?.isTrust;
          this.showIRAContributionType = itInfo.iraContributionType?.description !== '' && itInfo.iraContributionType?.description !== null;
          this.iraContributionType = this.formatContribType(this.itInfo.iraContributionType?.description);
          this.transactionTypeLabel = this.getTransactionTypeLabel(extraPrompts);
          let transactionDetails = form.transactionDetails;
          this.isRecurring = itInfo.recurringFields?.isRecurringTransaction;
          this.useStandingAuth = this.getStandingAuthValue(extraPrompts, itInfo),
          this.frequency = this.getFrequency(extraPrompts);
          this.amountType = amountTypeDict[transactionDetails.amountType];
          this.secondProcessDate = transactionDetails.recurringFieldsFormGroup?.recurringFrequency === 'SEMIMTH' ? transactionDetails.recurringFieldsFormGroup?.secondProcessDate : '';
          this.endDate = transactionDetails.recurringFieldsFormGroup?.endDate ?? '';
          this.frequencyDescription = this.isRecurring ? itInfo.recurringFields.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFieldsFormGroup.recurringFrequency)!.description : '';
          this.payIncome = this.isRecurring && transactionDetails.recurringFieldsFormGroup?.payIncome?.trim() ? this.titleCasePipe.transform(itInfo.recurringFields.payIncomeMap.find(payIncome => payIncome.code === transactionDetails.recurringFieldsFormGroup.payIncome)!.description) : '';
          this.amount = transactionDetails.amount;
          this.amountType = transactionDetails.amountType;
          this.nextProcessDate = itInfo.recurringFields.nextProcessDate ?? '';
          this.transferType = (typeof transferType === 'string') ? transferType : "";
          this.formTransferType = this.labels.cash;
        }
      }
    });
  }

  getTransactionTypeLabel(extraPrompts: ItExtraPrompts): string {
    let initialTransactionTypeLabel = this.isStandingAuthViewPage ? 'Internal cash transfers (journal)' : transactionTypesDict['internal-transfer'];
    if (extraPrompts.useStandingAuth === 'bundled'){
      return `${initialTransactionTypeLabel} - bundled`;
    }
    return initialTransactionTypeLabel;
  }

  getStandingAuthValue(extraPrompts: ItExtraPrompts, internalTransferInfo: InternalTransferInfo): YesNo | '' {
    // WPFA first-party journals should not be treated as using standing auth on review.
    if (internalTransferInfo.hasWpfaRole && !internalTransferInfo.isThirdParty) {
      return yesNo.no;
    }

    if (extraPrompts.useStandingAuth === 'bundled'){
      if (extraPrompts.useBundledStandingAuth === 'yes') return yesNo.yes;
      if (extraPrompts.useBundledStandingAuth === 'no' || extraPrompts.useBundledStandingAuth === 'recurring') return yesNo.no;
      return '';
    }
    return extraPrompts.useStandingAuth;
  }

 getFrequency(extraPrompts: ItExtraPrompts): string {
    // this indicates non-bundled flow
    if (extraPrompts.frequency) {
      return frequencyDict[extraPrompts.frequency!];
    }

    if (extraPrompts.useStandingAuth === 'bundled') {
      if (extraPrompts.useBundledStandingAuth === 'no') return frequencyDict['onetime'];
      if (extraPrompts.useBundledStandingAuth === 'recurring') return frequencyDict['recurring'];
    }
    return '';
  }

  get amountLabel(): string {
    return this.classifications.isPtrs || this.isToAccountTrust ? this.labels.trustAmount : this.labels.amount;
  }

  formatContribType(contribType: string){
    return contribType?.split(' ').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
  }

  get standingAuthLabel(): string {
    if (this.isBundledStandingAuth) {
      return this.labels.bundledStandingAuthDetails;
    }
    return this.labels.standingAuthDetails
  }
}
